/**
 * Where the booking flow reads and writes its checkout cart
 * (public.abandoned_trips).
 *
 * Migration 160 scoped the table to its owner: a signed-in customer reaches
 * only their own rows through PostgREST, and the anon key has no access at
 * all. A guest's cart therefore lives behind /api/guest-cart
 * (server/guest-carts.js), keyed by the booking session id this browser holds.
 * Both stores answer in supabase-js's `{ data, error }` shape, so callers keep
 * one set of merge rules whichever store they get.
 */
import { readStoredBookingSessionId } from '../lib/bookingSession';

const GUEST_CART_URL = '/api/guest-cart';

/** The session id each guest cart this tab has loaded belongs to, so a later
 *  update by cart id can prove it holds that cart's session. */
const guestCartSessions = new Map();

/** The store for whoever is browsing right now. */
export async function getCartStore() {
  const { supabase } = await import('../lib/supabase');
  const { data } = await supabase.auth.getSession();
  const userId = data?.session?.user?.id || null;
  return userId ? directCartStore(supabase, userId) : guestCartStore();
}

/** Signed in: PostgREST, where RLS keeps the customer to their own rows. */
export function directCartStore(supabase, userId) {
  const carts = () => supabase.from('abandoned_trips');
  return {
    isGuest: false,
    userId,
    /** The session's newest cart (sessions can hold duplicates). */
    async latestBySession(sessionId, columns = '*') {
      const { data, error } = await carts()
        .select(columns)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1);
      return { data: data?.[0] ?? null, error };
    },
    getById: (id, columns = '*') => carts().select(columns).eq('id', id).maybeSingle(),
    insert: (row) => carts().insert(row).select().single(),
    updateById: (id, patch) => carts().update(patch).eq('id', id).select().single(),
  };
}

/** Not signed in: the guest-cart routes. The server decides which columns a
 *  guest may write and read, so `columns` arguments are ignored here. */
export function guestCartStore({ fetchImpl = (...args) => fetch(...args) } = {}) {
  const sessionOf = (cartId) => guestCartSessions.get(cartId) || readStoredBookingSessionId();
  return {
    isGuest: true,
    userId: null,
    latestBySession: (sessionId) => guestCartRequest(fetchImpl, 'GET', { session_id: sessionId }),
    getById: (id) => guestCartRequest(fetchImpl, 'GET', { session_id: sessionOf(id), id }),
    insert: (row) => guestCartRequest(fetchImpl, 'POST', { session_id: row.session_id, cart: row }),
    updateById: (id, patch) => guestCartRequest(fetchImpl, 'PATCH', { session_id: sessionOf(id), id, cart: patch }),
  };
}

async function guestCartRequest(fetchImpl, method, body) {
  try {
    const isRead = method === 'GET';
    const query = isRead
      ? `?${new URLSearchParams(Object.entries(body).filter(([, value]) => value != null))}`
      : '';
    const res = await fetchImpl(`${GUEST_CART_URL}${query}`, {
      method,
      credentials: 'omit',
      ...(isRead ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { data: null, error: { message: payload?.error || `Guest cart request failed (${res.status})`, status: res.status } };
    }
    const cart = payload?.cart ?? null;
    if (cart?.id && cart.session_id) guestCartSessions.set(cart.id, cart.session_id);
    return { data: cart, error: null };
  } catch (e) {
    return { data: null, error: { message: e?.message || 'Guest cart request failed' } };
  }
}
